/* ============================================================
   Trasse Families — Billing.

   The pilot took no card, which changes what this page has to say. A school
   that reaches day 15 has a saved card and gets charged; a household reaches
   day 15 with nothing on file, so NOTHING happens automatically — the parent
   chooses to pay, or the household pauses. Saying that plainly is the job.

   It is also the page the subscription gate shows INSTEAD of the generic
   lockout: SubscriptionLockout offers a "mock pay" button that writes a fake
   30-day expiry straight to Firestore, which must never be within reach of a
   paying customer.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { FaCreditCard, FaPhoneAlt } from 'react-icons/fa';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { customToast } from '../../utils/customToast';
import { SUPPORT_EMAIL } from '../../utils/companyContact';
import { OrgSubscription } from '../../services/useOrgSubscription';
import {
  cancelSubscription, initializeCheckout, isPaymentsConfigured, resumeSubscription,
} from '../../services/paymentsApi';
import PaystackSplitScreen, { runPaystackCheckout } from '../PaystackSplitScreen';
import { bill, fmt, HOUSEHOLD_ANALYTICS_KES } from '../subscriptionModel';
import { FAM, FAM_GRADIENT, FAMILY_FONT_CSS, UI_FONT, DISPLAY_FONT, FAMILY_PILOT_DAYS } from './familyTheme';
import { daysUntil, fmtDay, useFamilyOrg } from './useFamilyOrg';

interface Props {
  orgCode: string;
  orgName: string;
  sub: OrgSubscription;
  /** True when the gate is rendering this instead of the console (locked). */
  standalone?: boolean;
}

interface PaymentRow {
  id: string;
  at: Date | null;
  amountKES: number | null;
  status: string;
  reference: string;
  cadence: string;
}

const MONTHLY = bill('basic', 1, 'household', 'month'); // 1200
const WITH_ANALYTICS = bill('basic', 1, 'household', 'month', 1); // 1800

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') return (v as { toDate: () => Date }).toDate();
  if (v instanceof Date) return v;
  return null;
}

function statusLabel(s: string): { text: string; tone: 'good' | 'warn' | 'plain' } {
  if (s === 'success') return { text: 'Paid', tone: 'good' };
  if (s === 'pilot_verification') return { text: 'Card check', tone: 'plain' };
  if (s === 'failed') return { text: 'Failed', tone: 'warn' };
  return { text: s || 'Recorded', tone: 'plain' };
}

/* ---------------------------------------------------------------- pieces -- */
const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <section style={{ background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20, padding: 'clamp(18px,3vw,26px)', ...style }}>
    {children}
  </section>
);

const CardTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 800, color: FAM.ink, letterSpacing: '-.01em' }}>{children}</h3>
);

export default function FamilyBillingPage({ orgCode, orgName, sub, standalone = false }: Props) {
  const { currentUser, logout } = useAuth();
  const org = useFamilyOrg(orgCode);

  const [showCheckout, setShowCheckout] = useState(false);
  const [busy, setBusy] = useState(false);
  const [justPaid, setJustPaid] = useState(false);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [historyError, setHistoryError] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [wantAnalytics, setWantAnalytics] = useState(false);

  useEffect(() => {
    if (org.analyticsAddon) setWantAnalytics(true);
  }, [org.analyticsAddon]);

  const due = wantAnalytics ? WITH_ANALYTICS : MONTHLY;

  const email = (org.adminEmail || currentUser?.email || '').trim().toLowerCase();

  /* Payment history. No orderBy: (orgCode ==) + (at desc) needs a composite
     index, and a missing index fails the whole listener — sorting 50 rows in
     the browser costs nothing and cannot break. */
  useEffect(() => {
    if (!orgCode) return;
    const q = query(collection(db, 'payments'), where('orgCode', '==', orgCode), limit(50));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setHistoryError(false);
        const rows: PaymentRow[] = snap.docs.map((d) => {
          const v = d.data() as Record<string, unknown>;
          return {
            id: d.id,
            at: toDate(v.at),
            amountKES: typeof v.amountKES === 'number' ? v.amountKES : null,
            status: String(v.status || ''),
            reference: String(v.reference || d.id),
            cadence: String(v.cadence || ''),
          };
        });
        rows.sort((a, b) => (b.at?.getTime() || 0) - (a.at?.getTime() || 0));
        setPayments(rows);
      },
      () => setHistoryError(true),
    );
    return () => unsub();
  }, [orgCode]);

  /* --------------------------------------------------------------- pay -- */
  const startCheckout = useCallback(async () => {
    if (!isPaymentsConfigured()) {
      customToast.show('Payments are temporarily unavailable. Please try again shortly.', 'error');
      return;
    }
    if (!email) {
      customToast.show(`We do not have an email for this household — contact ${SUPPORT_EMAIL}.`, 'error');
      return;
    }
    setBusy(true);
    try {
      const res = await initializeCheckout({
        orgCode, email, plan: 'basic', unit: 'household', size: 1,
        addonCount: (wantAnalytics || org.analyticsAddon) ? 1 : 0, cadence: 'monthly', currency: 'KES',
      });
      // The CTA quoted the list price; the backend may have priced a waiting
      // referral credit in. Say so before Paystack shows a different number —
      // a silent mismatch reads as a billing error (review, 09-06).
      if (typeof res.amountKES === 'number' && res.amountKES > 0 && res.amountKES < due) {
        customToast.show(
          `Referral discount applied — you'll be charged Ksh ${fmt(res.amountKES)} this month.`,
          'success',
        );
      }
      await runPaystackCheckout(res, {
        onSuccess: () => {
          setBusy(false);
          setShowCheckout(false);
          setJustPaid(true);
          customToast.show('Payment received — your household stays on.', 'success');
        },
        onDismiss: () => setBusy(false),
        onError: (msg) => customToast.show(`Payment error: ${msg}.`, 'error'),
      });
    } catch (err) {
      setBusy(false);
      const reason = err instanceof Error && err.message ? err.message : 'unexpected error';
      customToast.show(`Couldn't start the payment: ${reason}.`, 'error');
    }
  }, [email, orgCode, wantAnalytics, org.analyticsAddon, due]);

  /* ------------------------------------------------------ cancel/resume -- */
  async function doCancel() {
    setCancelBusy(true);
    const res = await cancelSubscription(orgCode, cancelReason.trim() || 'No reason given');
    setCancelBusy(false);
    if (!res.ok) { customToast.show(res.message || 'Could not cancel.', 'error'); return; }
    setCancelOpen(false);
    setCancelReason('');
    customToast.show(
      res.alreadyCanceled ? 'This household is already set to stop.' : 'Cancelled. You keep access to the end of the period you have paid for.',
      'success',
    );
  }

  async function doResume() {
    const res = await resumeSubscription(orgCode);
    if (!res.ok) { customToast.show(res.message || 'Could not resume.', 'error'); return; }
    customToast.show('Welcome back — your household will keep renewing.', 'success');
  }

  /* -------------------------------------------------------------- state -- */
  const pilotEnds = org.pilotEndsAt;
  /* Status from useOrgSubscription, the day count from pilotEndsAt — see the
     note in FamilyDashboard: expirationDate is one day past the last free day. */
  const isPilot = sub.isPilot || org.isPilot;
  const pilotLeft = pilotEnds ? daysUntil(pilotEnds) : sub.pilotDaysLeft;
  const locked = sub.status === 'locked';
  const nextCharge = org.firstChargeAt || sub.expirationDate;

  const headline = useMemo(() => {
    if (locked) return 'Your household is paused';
    if (justPaid) return 'Payment received';
    if (isPilot) return `${FAMILY_PILOT_DAYS} days free — ${pilotLeft} left`;
    if (org.cancelAtPeriodEnd) return 'Set to stop at the end of this month';
    return 'Your household is active';
  }, [locked, justPaid, isPilot, org.cancelAtPeriodEnd, pilotLeft]);

  const css = `
    ${FAMILY_FONT_CSS}
    .fbill { font-family: ${UI_FONT}; color: ${FAM.ink}; display: flex; flex-direction: column; gap: 18px; max-width: 1080px; width: 100%; margin: 0 auto; }
    .fbill-h { font-family: ${DISPLAY_FONT}; font-weight: 600; font-size: 34px; line-height: 1.1; letter-spacing: -.01em; margin: 6px 0 0; }
    @media (max-width: 600px) { .fbill-h { font-size: 26px; } }
    .fbill-cta { display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 46px; padding: 0 22px; border: none; border-radius: 999px; background: ${FAM_GRADIENT}; color: #fff; font: inherit; font-size: 15px; font-weight: 700; cursor: pointer; box-shadow: 0 14px 32px -14px rgba(0,112,127,.6); }
    .fbill-cta:disabled { opacity: .5; cursor: default; }
    .fbill-ghost { background: none; border: 1.5px solid ${FAM.line}; border-radius: 999px; height: 46px; padding: 0 20px; font: inherit; font-size: 14px; font-weight: 700; color: ${FAM.ink3}; cursor: pointer; }
    .fbill-ghost:hover { border-color: ${FAM.accentPale}; color: ${FAM.accent}; }
    .fbill-two { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
    @media (max-width: 700px) { .fbill-two { grid-template-columns: 1fr; } }
    .fbill-scroll { overflow-x: auto; }
    .fbill-tbl { width: 100%; border-collapse: collapse; font-size: 13.5px; min-width: 460px; }
    .fbill-tbl th { text-align: left; font-size: 11.5px; letter-spacing: .08em; text-transform: uppercase; color: ${FAM.muted}; font-weight: 800; padding: 0 12px 10px 0; }
    .fbill-tbl td { padding: 11px 12px 11px 0; border-top: 1px solid ${FAM.line}; color: ${FAM.ink}; }
  `;

  /* ---------------------------------------------------------- checkout -- */
  if (showCheckout) {
    return (
      <PaystackSplitScreen
        eyebrow="Amount due"
        amount={`Ksh ${fmt(due)}`}
        amountNote={<>Trasse Families · billed monthly{wantAnalytics ? <> · includes analytics</> : null}{isPilot && pilotEnds ? <> · your free days run to {fmtDay(pilotEnds)}</> : null}</>}
        rows={[
          { label: 'Household', value: orgName || orgCode },
          { label: 'Code', value: <span style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{orgCode}</span> },
          { label: 'Plan', value: wantAnalytics ? `Tracking + analytics · Ksh ${fmt(due)}` : `Tracking · Ksh ${fmt(due)}` },
          { label: 'Billing', value: 'Monthly' },
          { label: 'Receipt to', value: email || '—', faint: true },
        ]}
        payBlurb="Pay by card and it renews itself each month. Pay with M-PESA and we email you a link when the next month is due — M-PESA cannot authorise a repeat charge."
        ctaLabel={`Pay Ksh ${fmt(due)}`}
        onPay={startCheckout}
        busy={busy}
        onBack={() => setShowCheckout(false)}
        backLabel="Back to billing"
        footnote="Your card details never touch our servers. Cancel any time — you keep access to the end of the month you have paid for."
        accent={FAM.accent}
        accentGradient={FAM_GRADIENT}
      />
    );
  }

  /* ------------------------------------------------------------- page -- */
  const body = (
    <div className="fbill">
      <style>{css}</style>

      {/* ---- status ---- */}
      <Card style={locked ? { borderColor: '#f0dcb8', background: FAM.amberTint } : undefined}>
        <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.12em', color: locked ? FAM.amber : FAM.accent }}>
          {locked ? 'PAUSED' : isPilot ? 'FREE DAYS' : 'SUBSCRIPTION'}
        </div>
        <h2 className="fbill-h">{headline}</h2>

        {isPilot && !locked && (
          <>
            <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2 }}>
              Your free days run to <b style={{ color: FAM.ink }}>{fmtDay(pilotEnds)}</b>. You gave us no card
              to start, so nothing is charged when they end — that is your decision to make, not ours.
            </p>
            <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 14, background: FAM.tint, border: `1px solid ${FAM.tint2}` }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: FAM.accent }}>What happens on day {FAMILY_PILOT_DAYS + 1}</div>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13.5, lineHeight: 1.7, color: FAM.ink2 }}>
                <li>Nothing is taken from any card — we do not have one.</li>
                <li>You get a short grace window, and an email, to decide.</li>
                <li>If you do not pay, the dashboard pauses. Your runs, history and settings are kept, not deleted.</li>
                <li>Pay whenever you like and everything comes straight back on.</li>
              </ul>
            </div>
          </>
        )}

        {locked && (
          <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2 }}>
            The free days for <b style={{ color: FAM.ink }}>{orgName || orgCode}</b> have ended and no payment has
            been made yet. Everything you set up is safe and waiting — one payment brings it all back.
          </p>
        )}

        {!isPilot && !locked && (
          <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2 }}>
            {org.cancelAtPeriodEnd
              ? <>You have cancelled. Access runs to <b style={{ color: FAM.ink }}>{fmtDay(nextCharge)}</b> and then stops — nothing further is charged.</>
              : <>Ksh {fmt(due)} a month{wantAnalytics ? ', including analytics' : ''}. Next charge <b style={{ color: FAM.ink }}>{fmtDay(nextCharge)}</b>.</>}
          </p>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          {(isPilot || locked || org.cancelAtPeriodEnd) && (
            <button type="button" className="fbill-cta" onClick={() => setShowCheckout(true)}>
              Pay now · Ksh {fmt(due)}/mo
            </button>
          )}
          {org.cancelAtPeriodEnd
            ? <button type="button" className="fbill-ghost" onClick={doResume}>Keep my household</button>
            : !isPilot && !locked
              ? <button type="button" className="fbill-ghost" onClick={() => setCancelOpen(true)}>Cancel subscription</button>
              : null}
          {standalone && (
            <button type="button" className="fbill-ghost" onClick={() => logout()} style={{ marginLeft: 'auto' }}>Sign out</button>
          )}
        </div>
      </Card>

      {/* ---- the price, once, with no second tier to be confused by ---- */}
      <div className="fbill-two">
        <Card>
          <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.12em', color: FAM.accent }}>YOUR PLAN</div>
          <div className="fbill-h" style={{ fontSize: 27 }}>Ksh {fmt(due)}<span style={{ fontFamily: UI_FONT, fontSize: 15, fontWeight: 700, color: FAM.ink3 }}> /month</span></div>
          <p style={{ margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.65, color: FAM.ink2 }}>
            Live tracking, the driver app, run history and alerts, for two children. Analytics and extra children are Ksh {fmt(HOUSEHOLD_ANALYTICS_KES)} extra, and stay off until you pay for them.
          </p>
          <label style={{
            marginTop: 16, display: 'flex', gap: 10, alignItems: 'flex-start', cursor: org.analyticsAddon ? 'default' : 'pointer',
            padding: '12px 14px', borderRadius: 14, border: `1px solid ${wantAnalytics ? FAM.tint2 : FAM.line}`,
            background: wantAnalytics ? FAM.tint : '#FAF9F6',
          }}>
            <input
              type="checkbox"
              checked={wantAnalytics}
              disabled={org.analyticsAddon}
              onChange={(e) => setWantAnalytics(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={{ fontSize: 14, fontWeight: 800, color: FAM.ink }}>Analytics + extra children</span>
              <span style={{ display: 'block', marginTop: 3, fontSize: 13, lineHeight: 1.5, color: FAM.ink2 }}>
                {org.analyticsAddon
                  ? 'On. Analytics and unlimited children stay on your plan until you cancel the household.'
                  : `+ Ksh ${fmt(HOUSEHOLD_ANALYTICS_KES)} / month. Home tiles stay hidden until this is paid. The base plan covers two children; this lifts that cap.`}
              </span>
            </span>
          </label>
        </Card>
        <Card>
          <CardTitle>Card or M-PESA</CardTitle>
          <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'flex-start' }}>
            <FaCreditCard style={{ color: FAM.accent, marginTop: 3, flex: '0 0 auto' }} />
            <div style={{ fontSize: 13.5, lineHeight: 1.6, color: FAM.ink2 }}>
              <b style={{ color: FAM.ink }}>Card</b> — renews itself each month. Nothing to remember.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'flex-start' }}>
            <FaPhoneAlt style={{ color: FAM.pos2, marginTop: 3, flex: '0 0 auto' }} />
            <div style={{ fontSize: 13.5, lineHeight: 1.6, color: FAM.ink2 }}>
              <b style={{ color: FAM.ink }}>M-PESA</b> — an M-PESA authorisation cannot be reused, so we
              cannot charge you again automatically. Each month we email you a reminder with a payment
              link instead. Same price, one tap, nothing taken without you.
            </div>
          </div>
        </Card>
      </div>

      {/* ---- history ---- */}
      <Card>
        <CardTitle>Payments</CardTitle>
        {historyError ? (
          <p style={{ margin: '12px 0 0', fontSize: 13.5, color: FAM.amber }}>
            We could not load your payment history just now. Refresh the page — nothing is wrong with your household.
          </p>
        ) : payments.length === 0 ? (
          <p style={{ margin: '12px 0 0', fontSize: 13.5, color: FAM.muted }}>
            Nothing yet — your free days took no payment.
          </p>
        ) : (
          <div className="fbill-scroll" style={{ marginTop: 14 }}>
            <table className="fbill-tbl">
              <thead>
                <tr><th>Date</th><th>Amount</th><th>Status</th><th>Reference</th></tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const s = statusLabel(p.status);
                  return (
                    <tr key={p.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDay(p.at)}</td>
                      <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{p.amountKES == null ? '—' : `Ksh ${fmt(p.amountKES)}`}</td>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 999,
                          fontSize: 11.5, fontWeight: 800,
                          background: s.tone === 'good' ? FAM.posTint : s.tone === 'warn' ? FAM.dangerTint : FAM.tint,
                          color: s.tone === 'good' ? FAM.pos : s.tone === 'warn' ? FAM.danger : FAM.ink3,
                        }}>{s.text}</span>
                      </td>
                      <td style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, color: FAM.muted, wordBreak: 'break-all' }}>{p.reference}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---- cancel ---- */}
      {cancelOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(27,21,51,.44)', display: 'grid', placeItems: 'center', padding: 20, zIndex: 1000 }}>
          <div style={{ width: '100%', maxWidth: 460, background: '#fff', borderRadius: 20, padding: 26, fontFamily: UI_FONT }}>
            <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>Cancel Trasse Families?</h3>
            <p style={{ margin: '10px 0 0', fontSize: 14, lineHeight: 1.6, color: FAM.ink2 }}>
              You keep everything until <b>{fmtDay(nextCharge)}</b> — the end of the month you have already
              paid for — and are not charged again. Your runs and history stay in place if you come back.
            </p>
            <label style={{ display: 'block', marginTop: 16, fontSize: 12.5, fontWeight: 700, color: FAM.ink3 }}>
              What made you stop? (optional, but it is the only way we learn)
            </label>
            <textarea
              value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3}
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '11px 13px', borderRadius: 12, border: `1.5px solid ${FAM.line}`, font: 'inherit', fontSize: 14, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button type="button" className="fbill-ghost" onClick={() => setCancelOpen(false)}>Keep it</button>
              <button type="button" className="fbill-cta" disabled={cancelBusy} onClick={doCancel}
                style={{ background: FAM.danger, boxShadow: 'none' }}>
                {cancelBusy ? 'Cancelling…' : 'Cancel my subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (!standalone) return body;

  /* Locked: this page IS the whole screen, so it brings its own frame. */
  return (
    <div style={{ minHeight: '100vh', background: FAM.bg, fontFamily: UI_FONT }}>
      <style>{`${FAMILY_FONT_CSS}`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 58, padding: '0 clamp(14px,4vw,26px)', background: 'rgba(255,255,255,.8)', borderBottom: `1px solid ${FAM.line}` }}>
        <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-.02em' }}>Trasse</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 6, background: FAM.tint, color: FAM.accent, fontSize: 11, fontWeight: 800, letterSpacing: '.06em' }}>FAMILIES</span>
      </div>
      <div style={{ padding: 'clamp(20px,4vw,40px)' }}>{body}</div>
    </div>
  );
}
